
import { useState } from 'react';

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function PropertyDiagnosisForm() {
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Validate textarea length
    const notes = data.get('notes') as string;
    if (notes && notes.length > 500) {
      return;
    }

    setSubmitStatus('submitting');

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6hsht057726vudt82jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div id="property-diagnosis-form" className="mt-10 bg-gradient-to-br from-emerald-50 to-slate-50 rounded-3xl border-2 border-emerald-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-10 py-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
            <i className="ri-home-search-line text-white text-2xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-3 py-0.5 bg-white/20 text-white text-xs font-bold rounded-full whitespace-nowrap">無料</span>
              <span className="text-emerald-100 text-sm font-semibold">物件収益診断</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1">
              実際の物件で試算してもらえる
            </h3>
            <p className="text-emerald-100 text-sm leading-relaxed">
              物件情報を入力するだけで、プロが個別に収益シミュレーションをご提案。<br className="hidden md:block" />
              完全無料・返信は通常24時間以内です。
            </p>
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap gap-3 mt-6">
          {[
            { icon: 'ri-shield-check-line', label: '完全無料・費用一切なし' },
            { icon: 'ri-time-line', label: '24時間以内に返信' },
            { icon: 'ri-lock-line', label: '個人情報は厳重管理' },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5">
              <i className={`${b.icon} text-white text-sm`}></i>
              <span className="text-white text-xs font-medium whitespace-nowrap">{b.label}</span>
            </div>
          ))}
        </div>

        {/* Flow indicator */}
        <div className="mt-6 flex items-center gap-2 bg-white/10 rounded-2xl px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-white/30 rounded-full text-white text-xs font-bold shrink-0">1</div>
            <span className="text-white text-xs font-semibold whitespace-nowrap">物件情報を入力</span>
          </div>
          <i className="ri-arrow-right-line text-emerald-200 text-sm shrink-0"></i>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-white/30 rounded-full text-white text-xs font-bold shrink-0">2</div>
            <span className="text-white text-xs font-semibold whitespace-nowrap">収益診断レポート作成</span>
          </div>
          <i className="ri-arrow-right-line text-emerald-200 text-sm shrink-0"></i>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-emerald-400 rounded-full text-white text-xs font-bold shrink-0">3</div>
            <span className="text-emerald-200 text-xs font-bold whitespace-nowrap">診断結果をもとにプランを提案</span>
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="px-10 py-10">
        {submitStatus === 'success' ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-4xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-slate-900 mb-3">送信完了しました！</h4>
            <p className="text-slate-600 leading-relaxed mb-6">
              ご入力いただいた物件情報をもとに、<br />
              担当者が個別の収益シミュレーションを作成してご連絡します。<br />
              通常24時間以内にご返信いたします。
            </p>
            {/* Next step hint */}
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-4 mb-6 text-left">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
                <i className="ri-lightbulb-flash-line text-emerald-600 text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">次のステップ</p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  診断結果をもとに最適なプランをご提案します。<br />
                  担当者からのご連絡をお待ちください。
                </p>
              </div>
            </div>
            <button
              onClick={() => setSubmitStatus('idle')}
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              別の物件を診断する
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="property-diagnosis-form-element"
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Row 1: Name + Email */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  お名前 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="山田 太郎"
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  メールアドレス <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="example@email.com"
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200"
                />
              </div>
            </div>

            {/* Row 2: Phone + Property type */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  電話番号
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="090-0000-0000"
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  物件タイプ <span className="text-rose-500">*</span>
                </label>
                <select
                  name="property_type"
                  required
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200 cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="1R/1K">1R / 1K</option>
                  <option value="1LDK">1LDK</option>
                  <option value="2LDK">2LDK</option>
                  <option value="3LDK以上">3LDK以上</option>
                  <option value="一戸建て">一戸建て</option>
                  <option value="その他">その他</option>
                </select>
              </div>
            </div>

            {/* Row 3: Location + Area */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  物件の所在地（エリア） <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  required
                  placeholder="例：東京都品川区馬込"
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  専有面積（㎡）
                </label>
                <input
                  type="text"
                  name="area"
                  placeholder="例：35㎡"
                  className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200"
                />
              </div>
            </div>

            {/* Row 4: Current status */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                現在の物件状況 <span className="text-rose-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { value: '空室（すぐ開始可能）', icon: 'ri-home-line' },
                  { value: '賃貸中（将来的に転換予定）', icon: 'ri-building-line' },
                  { value: 'すでに民泊運営中', icon: 'ri-store-line' },
                  { value: '物件を探している段階', icon: 'ri-search-line' },
                ].map((opt) => (
                  <label key={opt.value} className="flex flex-col items-center gap-2 p-4 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200 has-[:checked]:border-emerald-500 has-[:checked]:bg-emerald-50">
                    <input type="radio" name="current_status" value={opt.value} required className="sr-only" />
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${opt.icon} text-slate-500 text-xl`}></i>
                    </div>
                    <span className="text-xs font-medium text-slate-700 text-center leading-tight">{opt.value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Row 5: Goal */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                民泊運営の目標
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  '月10〜30万円の副収入',
                  '月50〜100万円の本格運営',
                  '月100万円超の高収益モデル',
                ].map((goal) => (
                  <label key={goal} className="flex items-center gap-3 p-4 border-2 border-slate-200 rounded-xl cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-all duration-200 has-[:checked]:border-emerald-500 has-[:checked]:bg-emerald-50">
                    <input type="radio" name="goal" value={goal} className="w-4 h-4 accent-emerald-500" />
                    <span className="text-sm font-medium text-slate-700">{goal}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Row 6: Notes */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                ご質問・ご要望（任意）
              </label>
              <textarea
                name="notes"
                rows={4}
                maxLength={500}
                placeholder="物件の特徴、気になること、ご要望などをご自由にお書きください。"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-4 py-3 text-sm border-2 border-slate-200 rounded-xl focus:outline-none focus:border-emerald-400 bg-white transition-colors duration-200 resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-rose-500' : 'text-slate-400'}`}>
                {charCount} / 500文字
              </p>
            </div>

            {/* Error message */}
            {submitStatus === 'error' && (
              <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl">
                <i className="ri-error-warning-line text-rose-500 text-xl flex-shrink-0"></i>
                <p className="text-sm text-rose-600">送信に失敗しました。時間をおいて再度お試しください。</p>
              </div>
            )}

            {/* Submit */}
            <div className="flex flex-col md:flex-row items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="w-full md:w-auto flex items-center justify-center gap-2 px-10 py-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {submitStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line text-xl animate-spin"></i>
                    <span>送信中...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-xl"></i>
                    <span>無料で収益診断を依頼する</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>
              <p className="text-xs text-slate-400 text-center md:text-left">
                送信後、担当者より個別にご連絡いたします。<br />
                営業・勧誘は一切ありません。
              </p>
            </div>

            {/* Post-submit flow hint */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mt-2">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-information-line text-amber-500 text-lg"></i>
              </div>
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-bold">診断後の流れ：</span>
                収益診断レポートをお届けした後、<strong>診断結果をもとに最適なプランをご提案</strong>します。
                無理な勧誘は一切なく、ご納得いただいた上でご検討いただけます。
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
